/**
 * ShopifyNavMenu — registers AI Optimizer's pages in Shopify Admin's native
 * left-sidebar navigation using App Bridge v4 NavMenu.
 *
 * Outside a Shopify Admin iframe: renders nothing (no-op).
 * Inside Shopify Admin: Shopify renders the links in its native sidebar,
 *   highlights the active item based on window.location.pathname,
 *   and navigates the iframe to the href when clicked.
 *
 * No Provider / apiKey / host needed — App Bridge v4 is auto-loaded by Shopify.
 * The CDN script in public/index.html initialises window.shopify which NavMenu uses.
 */

import React from 'react';
import { NavMenu } from '@shopify/app-bridge-react';
import { useRuntime } from '../context/RuntimeContext';

/** True only when running inside a cross-origin iframe (i.e. Shopify Admin). */
function inShopifyAdminFrame() {
  try {
    return window.self !== window.top;
  } catch (_) {
    // SecurityError accessing window.top → definitely cross-origin iframe
    return true;
  }
}

// Route table — single source of truth for page ID ↔ URL path ↔ nav label.
// Keep in sync with the `pages` map in App.js.
export const NAV_ROUTES = [
  { id: 'home',           path: '/',                labelKey: 'nav.overview',     labelFb: 'Dashboard',        home: true },
  { id: 'products',       path: '/catalog',          labelKey: 'nav.catalog',      labelFb: 'Catalog'                      },
  { id: 'content',        path: '/content',          labelKey: 'nav.content',      labelFb: 'AI Content'                   },
  { id: 'translations',   path: '/translations',     labelKey: 'nav.translations', labelFb: 'Translations'                 },
  { id: 'images',         path: '/images',           labelKey: 'nav.images',       labelFb: 'Images'                       },
  { id: 'batch',          path: '/batch',            labelKey: 'nav.batch',        labelFb: 'Batch Operations'             },
  { id: 'speed',          path: '/speed',            labelKey: 'nav.speed',        labelFb: 'Speed Optimizer'              },
  { id: 'search-console', path: '/search-console',   labelKey: 'nav.searchConsole',labelFb: 'Search Console'              },
  { id: 'history',        path: '/history',          labelKey: 'nav.history',      labelFb: 'History'                      },
  { id: 'settings',       path: '/settings',         labelKey: 'nav.settings',     labelFb: 'Settings'                     },
  { id: 'support',        path: '/support',          labelKey: 'nav.support',      labelFb: 'Support'                      },
];

/**
 * Resolve the active page ID from the current URL.
 * Priority:
 *   1. Shopify billing return params (?purchase_id= | ?subscription_return=) → 'settings'
 *   2. window.location.pathname  (primary: path-based routing)
 *   3. ?page= query param        (backward compat: Shopify redirect URLs)
 *   4. localStorage              (last visited page fallback)
 *   5. 'home'                    (default)
 */
export function resolvePageFromUrl() {
  const params = new URLSearchParams(window.location.search);

  // Billing return flows always land on Settings
  if (params.get('purchase_id') || params.get('subscription_return')) return 'settings';

  // Path-based match (primary)
  const pathname = window.location.pathname;
  if (pathname && pathname !== '/') {
    const byPath = NAV_ROUTES.find(
      (r) => !r.home && r.path !== '/' && pathname.startsWith(r.path),
    );
    if (byPath) return byPath.id;
  }

  // Legacy ?page= param (backward compat)
  const pageParam = params.get('page');
  if (pageParam) {
    const byParam = NAV_ROUTES.find((r) => r.id === pageParam);
    if (byParam) return byParam.id;
  }

  // localStorage fallback (last visited page)
  try {
    const saved = localStorage.getItem('aio_last_route');
    if (saved && NAV_ROUTES.find((r) => r.id === saved)) return saved;
  } catch (_) { /* private / incognito */ }

  return 'home';
}

/** Return the URL path for a given page ID. */
export function pathForPage(pageId) {
  return NAV_ROUTES.find((r) => r.id === pageId)?.path ?? '/';
}

/**
 * ShopifyNavMenu — render the App Bridge NavMenu with i18n labels.
 * Must be rendered inside RuntimeProvider so useRuntime() is available.
 * Returns null when not inside a Shopify Admin iframe to avoid rendering
 * nav links as visible HTML in standalone / development contexts.
 */
export function ShopifyNavMenu() {
  const { t } = useRuntime();

  if (!inShopifyAdminFrame()) return null;

  return (
    <NavMenu>
      {NAV_ROUTES.map((route) => (
        <a
          key={route.id}
          href={route.path}
          {...(route.home ? { rel: 'home' } : {})}
        >
          {t(route.labelKey, route.labelFb)}
        </a>
      ))}
    </NavMenu>
  );
}

export default ShopifyNavMenu;
